import { useContext, useEffect, useState } from "react";

import {
  ArrowBack,
  Campaign,
  CheckCircle,
  CloudDownload,
  ContentCopy,
  MoreVert,
  PostAdd,
} from "@mui/icons-material";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Paper,
  Tooltip,
  Typography,
} from "@mui/material";
import { SubmissionPartner, getSubmissionPartner } from "@rubi/api-generated";
import { PageHeader } from "@rubi/ui";
import { toast } from "@rubi/ui";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import { Model } from "survey-core";

import { formatDateShort } from "@/common/utils";
import CondensedReportSurvey from "@/components/online-reporting/report-view/CondensedReportSurvey";
import condensedStyles from "@/components/online-reporting/report-view/CondensedReportView.module.scss";
import SectionHeader from "@/components/online-reporting/report-view/SectionHeader";
import { getReportTitle } from "@/components/online-reporting/report-view/utils";
import {
  applyRedactedFieldDisplay,
  createCondensedModel,
} from "@/components/online-reporting/utils";
import { parseUserPermissions } from "@/components/UserManagement/utils";
import { AuthStateContext } from "@/contexts/AuthContext";
import { useDevEnvironment } from "@/contexts/DevContext";
import { APP_OR_SERVER_URL } from "@/environment";
import { IUserPermission } from "@/types/typesAlerts";
import { ReportStatus, reportColourTypes } from "@/types/typesReportV2";

import {
  partnerStatusTooltips,
  partnerStatusTypes,
} from "./partnerStatusLabels";

/** Incident start date-time answer keys in precedence order; first present, non-empty value wins. */
const INCIDENT_START_KEYS = [
  "incident_startDateTime",
  "incident_dateTime",
  "incident_startDate",
] as const;

/** Reads the incident's start time so the header shows when it occurred, not when it was submitted. */
const getIncidentStartTime = (submission: unknown): string | undefined => {
  if (!submission || typeof submission !== "object") return undefined;
  const answers = submission as Record<string, unknown>;
  for (const key of INCIDENT_START_KEYS) {
    const value = answers[key];
    if (typeof value === "string" && value.trim() !== "") return value;
  }
  return undefined;
};

const CopyableChip = ({
  tooltip,
  value,
}: {
  tooltip: string;
  value: string | number;
}) => (
  <Box sx={{ display: "inline-flex", alignItems: "center" }}>
    <Tooltip title={tooltip} arrow>
      <Chip
        label={value}
        size="small"
        sx={{
          backgroundColor: "action.hover",
          color: "text.primary",
          fontSize: { xs: "13px", sm: "14px" },
          height: { xs: "18px", sm: "20px" },
          borderRadius: "4px",
        }}
      />
    </Tooltip>
    <IconButton
      size="small"
      onClick={() => navigator.clipboard.writeText(String(value))}
      title={`Copy ${tooltip}`}
      sx={{ padding: "1px", width: 20, height: 20 }}
    >
      <ContentCopy fontSize="small" sx={{ color: "text.secondary" }} />
    </IconButton>
  </Box>
);

// Auth rides the session cookie, so a plain window navigation is enough to download the PDF.
const partnerPdfUrl = (
  submissionId: string,
  combined = false,
  partnerOrgId?: string | null,
) => {
  const params = new URLSearchParams();
  if (combined) params.set("combined", "true");
  if (partnerOrgId) params.set("partner_org_id", partnerOrgId);
  const query = params.toString();
  return `${APP_OR_SERVER_URL}/report/partner/submission/${submissionId}/pdf${
    query ? `?${query}` : ""
  }`;
};

const downloadPartnerPdf = (
  submissionId: string,
  combined = false,
  partnerOrgId?: string | null,
) => window.open(partnerPdfUrl(submissionId, combined, partnerOrgId), "_self");

const CardActionsMenu = ({
  submissionId,
  partnerOrgId,
}: {
  submissionId: string;
  partnerOrgId?: string | null;
}) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  return (
    <>
      <IconButton
        size="small"
        aria-label="More options"
        onClick={(e) => setAnchorEl(e.currentTarget)}
        sx={{ color: "text.secondary" }}
      >
        <MoreVert fontSize="small" />
      </IconButton>
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <MenuItem
          onClick={() => {
            setAnchorEl(null);
            downloadPartnerPdf(submissionId, false, partnerOrgId);
          }}
        >
          <ListItemIcon>
            <CloudDownload fontSize="small" />
          </ListItemIcon>
          <ListItemText>Download PDF</ListItemText>
        </MenuItem>
      </Menu>
    </>
  );
};

const PartnerReportDetail = () => {
  const { orgId, id } = useParams();
  const navigate = useNavigate();
  const { isDevEnv } = useDevEnvironment();
  const { currentUser } = useContext(AuthStateContext);

  const { t } = useTranslation("reportsv2/condensed_report");

  const [report, setReport] = useState<SubmissionPartner | null>(null);
  const [rendered, setRendered] = useState<
    { report: SubmissionPartner; survey: Model }[]
  >([]);
  const [loading, setLoading] = useState(true);

  const permissions = currentUser
    ? parseUserPermissions(currentUser.permissions as IUserPermission[])
    : null;
  const canAddSupplemental = permissions?.canAddPartnerSupplemental ?? false;

  useEffect(() => {
    let active = true;
    const load = async () => {
      if (!id) return;
      setReport(null);
      setRendered([]);
      setLoading(true);
      try {
        const data = (
          await getSubmissionPartner({
            path: { submission_id: id },
            query: { partner_org_id: orgId },
          })
        ).data;
        if (!active || !data) return;
        setReport(data);
        // Build a condensed card per report: its linked supplementals (newest first) above the original.
        const supplementals =
          (data as SubmissionPartner & { supplementals?: SubmissionPartner[] })
            .supplementals ?? [];
        const built: { report: SubmissionPartner; survey: Model }[] = [];
        for (const r of [...supplementals, data]) {
          const { newSurvey } = await createCondensedModel(
            r.survey_structure,
            r.submission ?? {},
            r.attachments,
            true,
            r.is_supplemental,
            false,
            r.submission_id,
            r.survey_class,
            r.time_submitted ?? r.time_created,
          );
          applyRedactedFieldDisplay(newSurvey, t("redacted_for_privacy"));
          built.push({ report: r, survey: newSurvey });
        }
        if (active) setRendered(built);
      } catch (e) {
        const message = e instanceof Error ? e.message : "";
        toast.error(`Failed to load report${isDevEnv ? `: ${message}` : ""}`);
      } finally {
        if (active) setLoading(false);
      }
    };
    load();
    return () => {
      active = false;
    };
  }, [id, isDevEnv, orgId]);

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", mt: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!report) {
    return (
      <Box sx={{ p: 4 }}>
        <Button startIcon={<ArrowBack />} onClick={() => navigate(-1)}>
          Back
        </Button>
        <Typography sx={{ mt: 2 }}>Report not found.</Typography>
      </Box>
    );
  }

  const status = report.status as ReportStatus;

  const answers = (report.submission ?? {}) as Record<string, unknown>;
  const lcboIncidentNumber = answers["incident_storeIncidentNumber"] as
    | string
    | undefined;
  const storeNumber = (
    answers["business-lcboLocation"] as { store?: string | number } | undefined
  )?.store;

  return (
    <>
      <PageHeader
        measureHeight
        centerMiddle
        onBack={() => navigate(`/rubipartner/${orgId}/reports`)}
        left={
          <Tooltip title={partnerStatusTooltips[status] || ""} arrow>
            <Chip
              label={partnerStatusTypes[status] ?? status}
              color={reportColourTypes[status] ?? "default"}
            />
          </Tooltip>
        }
        middle={
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: 0.5,
              minWidth: 0,
            }}
          >
            <Typography
              variant="h6"
              component="h1"
              sx={{ fontWeight: "bold", lineHeight: 1.2 }}
            >
              {getReportTitle(report.is_supplemental, report.survey_name ?? "")}
            </Typography>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: { xs: "4px", sm: "6px" },
                flexWrap: "wrap",
                justifyContent: "center",
              }}
            >
              {report.tracking_number && (
                <CopyableChip
                  tooltip="Tracking number"
                  value={report.tracking_number}
                />
              )}
              {lcboIncidentNumber && (
                <CopyableChip
                  tooltip="LCBO incident number"
                  value={lcboIncidentNumber}
                />
              )}
              {storeNumber != null && storeNumber !== "" && (
                <CopyableChip tooltip="Store number" value={storeNumber} />
              )}
            </Box>
          </Box>
        }
        right={
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            {canAddSupplemental && report.supplemental_url && (
              <>
                {report.has_supplemental && (
                  <Tooltip title="Supplemental attached" arrow>
                    <CheckCircle color="success" />
                  </Tooltip>
                )}
                <Button
                  variant={report.has_supplemental ? "outlined" : "contained"}
                  startIcon={<PostAdd />}
                  onClick={() =>
                    window.open(
                      report.supplemental_url!,
                      "_blank",
                      "noopener,noreferrer",
                    )
                  }
                >
                  Add Supplemental
                </Button>
              </>
            )}
            <Tooltip
              title="Create bulletin in RubiALERT (not available on this surface)"
              arrow
            >
              <span>
                <Button
                  disabled
                  variant="outlined"
                  startIcon={<Campaign />}
                  sx={{
                    textTransform: "uppercase",
                    fontWeight: 600,
                    "&.Mui-disabled": {
                      backgroundColor: "transparent",
                      color: "#999",
                      border: "2px solid #ccc",
                    },
                  }}
                >
                  Create Bulletin
                </Button>
              </span>
            </Tooltip>
            <Tooltip title="Download all (report + supplementals)" arrow>
              <IconButton
                color="primary"
                aria-label="Download all"
                onClick={() =>
                  downloadPartnerPdf(report.submission_id, true, orgId)
                }
              >
                <CloudDownload />
              </IconButton>
            </Tooltip>
          </Box>
        }
      />
      <Box
        sx={{
          maxWidth: "var(--page-max-width)",
          margin: "0 auto",
          width: "100%",
          p: 2,
        }}
      >
        {rendered.map(({ report: r, survey }) => {
          const incidentStart = getIncidentStartTime(r.submission);
          return (
            <Paper
              key={r.submission_id}
              elevation={3}
              className={condensedStyles.paperContainer}
              style={{
                border: "1px solid #e0e0e0",
                paddingTop: 0,
                marginBottom: 16,
              }}
            >
              <div
                style={{
                  position: "sticky",
                  /**
                   * 1px overlap into the PageHeader so a sub-pixel measurement never leaves a seam
                   * for content to show through.
                   */
                  top: "calc(var(--top-bar-height, 70px) - 1px)",
                  zIndex: 9,
                  // Bleed to card edges: must match .paperContainer's 8px padding.
                  margin: "0 -8px 16px -8px",
                  padding: "4px 8px 0 8px",
                  // Opaque paper with the hover tint on top: #f5f5f5 in light
                  // mode, a matching raised grey in dark. Sticky, so it must not
                  // be translucent.
                  background:
                    "linear-gradient(var(--action-hover), var(--action-hover)), var(--background-paper)",
                  borderTopLeftRadius: "inherit",
                  borderTopRightRadius: "inherit",
                }}
              >
                <SectionHeader
                  title={
                    r.is_supplemental
                      ? t("supplemental_report")
                      : t("incident_report")
                  }
                  subtitle={
                    <>
                      {t("version")}:{r.version}
                      {incidentStart
                        ? ` - ${t("incident_start_time")}: ${formatDateShort(incidentStart)}`
                        : ""}
                    </>
                  }
                  actions={
                    <CardActionsMenu
                      submissionId={r.submission_id}
                      partnerOrgId={orgId}
                    />
                  }
                />
              </div>
              <CondensedReportSurvey model={survey} />
            </Paper>
          );
        })}
      </Box>
    </>
  );
};

export default PartnerReportDetail;
